import type { FC, ReactNode } from 'react'

import {
    ArrowRight,
    Check,
    Minus,
    MonitorOff,
    Shuffle,
    UsersRound
} from 'lucide-react'

import { StatusLabel } from '@/components/generals/status-label'
import { BuckyMascot } from '@/components/generals/bucky-mascot'
import { businessIcons } from '@/components/landing/business-icons'

import { businesses, concept } from '@/constants/content'

const problemIcons = [UsersRound, MonitorOff, Shuffle]

interface SubHeadingProps {
    title: string
    intro?: string
    eyebrow?: string
}

const SubHeading: FC<SubHeadingProps> = ({ title, intro, eyebrow }): ReactNode => (
    <div className='reveal max-w-2xl'>
        {eyebrow && (
            <p className='mb-3 text-xs font-semibold tracking-[0.18em] text-orange uppercase'>
                {eyebrow}
            </p>
        )}

        <h3 className='text-[1.75rem] leading-tight font-bold tracking-tight text-soft-white sm:text-4xl'>
            {title}
        </h3>

        {intro && (
            <p className='mt-3 text-lg leading-relaxed text-cool-grey-light'>
                {intro}
            </p>
        )}
    </div>
)

export const ConceptProblem: FC = (): ReactNode => (
    <div id='bucky-problem' className='mt-24 sm:mt-32'>
        <SubHeading title={concept.problem.heading} />

        <ul className='mt-10 grid gap-5 md:grid-cols-3'>
            {concept.problem.items.map((item, index) => {
                const Icon = problemIcons[index]

                return (
                    <li key={item.title} className='reveal rounded-xl border border-soft-white/10 bg-navy-900/60 p-6'>
                        <span className='grid size-11 place-items-center rounded-xl bg-navy-800 text-orange ring-1 ring-soft-white/10'>
                            <Icon className='size-5' aria-hidden />
                        </span>

                        <h4 className='mt-5 font-heading text-lg font-bold text-soft-white'>
                            {item.title}
                        </h4>

                        <ul className='mt-3 flex flex-col gap-2.5'>
                            {item.points.map(point => (
                                <li key={point} className='flex gap-2.5 text-sm leading-relaxed text-cool-grey-light'>
                                    <span aria-hidden className='mt-2 size-1.5 shrink-0 rounded-full bg-orange/80' />
                                    {point}
                                </li>
                            ))}
                        </ul>
                    </li>
                )
            })}
        </ul>
    </div>
)

export const ConceptDifference: FC = (): ReactNode => {
    const { difference } = concept

    return (
        <div id='bucky-difference' className='mt-24 sm:mt-32'>
            <SubHeading eyebrow={difference.eyebrow} title={difference.heading} />

            <div className='mt-10 grid gap-5 lg:grid-cols-[0.85fr_1.15fr]'>
                <div className='reveal rounded-xl border border-soft-white/10 bg-navy-900/60 p-7 sm:p-8'>
                    <h4 className='text-lg font-semibold text-cool-grey-light'>
                        {difference.chatbot.title}
                    </h4>

                    <ul className='mt-6 flex flex-col gap-4'>
                        {difference.chatbot.points.map(point => (
                            <li key={point} className='flex items-start gap-3 text-cool-grey-light'>
                                <span className='mt-0.5 grid size-6 shrink-0 place-items-center rounded-full bg-soft-white/5 ring-1 ring-soft-white/10'>
                                    <Minus className='size-3.5' aria-hidden />
                                </span>
                                {point}
                            </li>
                        ))}
                    </ul>
                </div>

                <div className='reveal relative overflow-hidden rounded-xl border border-orange/30 bg-[linear-gradient(160deg,#1B3350,#14283D_60%)] p-7 shadow-[0_30px_80px_-40px_rgb(255_138_61/0.45)] sm:p-8'>
                    <div aria-hidden className='absolute -top-24 -right-24 size-64 rounded-full bg-orange/10 blur-3xl' />

                    <div className='relative flex items-center gap-4'>
                        <BuckyMascot size={48} decorative />

                        <h4 className='text-lg font-semibold text-soft-white'>
                            {difference.bucky.title}
                        </h4>
                    </div>

                    <ul className='relative mt-6 flex flex-col gap-4'>
                        {difference.bucky.points.map(point => (
                            <li key={point} className='flex items-start gap-3 text-soft-white/90'>
                                <span className='mt-0.5 grid size-6 shrink-0 place-items-center rounded-full bg-orange text-navy'>
                                    <Check className='size-3.5' aria-hidden />
                                </span>
                                {point}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <p className='mt-5 text-sm text-cool-grey-light'>
                {difference.note}
            </p>
        </div>
    )
}

export const ConceptIndustries: FC = (): ReactNode => (
    <div id='bucky-industries' className='mt-24 sm:mt-32'>
        <SubHeading title={concept.industries.heading} intro={concept.industries.intro} />

        <ul className='mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-4'>
            {businesses.items.map(item => {
                const Icon = businessIcons[item.icon]

                return (
                    <li key={item.id} className='reveal flex gap-3 rounded-xl border border-soft-white/10 bg-navy-900/50 p-4'>
                        <span className='grid size-10 shrink-0 place-items-center rounded-lg bg-navy-800 text-orange ring-1 ring-soft-white/10'>
                            <Icon className='size-5' aria-hidden />
                        </span>

                        <div>
                            <h4 className='font-heading text-sm font-bold text-soft-white'>
                                {item.name}
                            </h4>

                            <p className='mt-1 text-sm leading-relaxed text-cool-grey-light'>
                                {item.explanation}
                            </p>
                        </div>
                    </li>
                )
            })}

            <li className='reveal'>
                <a
                    href='#businesses'
                    className='flex h-full min-h-20 items-center justify-between gap-3 rounded-xl border border-orange/40 bg-orange/10 p-4 font-semibold text-soft-white transition hover:bg-orange/20'
                >
                    {concept.industries.cta}
                    <ArrowRight className='size-5 text-orange' aria-hidden />
                </a>
            </li>
        </ul>

        <p className='mt-4 text-xs text-cool-grey-light'>
            {businesses.label}
        </p>
    </div>
)

export const ConceptPlatform: FC = (): ReactNode => {
    const { platform } = concept

    return (
        <div id='bucky-platform' className='mt-24 sm:mt-32'>
            <SubHeading title={platform.heading} intro={platform.intro} />

            <ul className='mt-10 divide-y divide-soft-white/10 overflow-hidden rounded-2xl border border-soft-white/10 bg-navy-900/50'>
                {platform.items.map((item, index) => (
                    <li key={item.name} className='reveal grid gap-3 p-5 sm:grid-cols-[3rem_1fr_auto] sm:items-center sm:gap-5 sm:p-6'>
                        <span className='font-heading text-sm font-bold text-cool-grey-light'>
                            {String(index + 1).padStart(2, '0')}
                        </span>

                        <div>
                            <h4 className='font-heading text-lg font-bold text-soft-white'>
                                {item.name}
                            </h4>

                            <p className='mt-1 text-sm leading-relaxed text-cool-grey-light'>
                                {item.text}
                            </p>
                        </div>

                        <StatusLabel status={item.status} className='justify-self-start sm:justify-self-end' />
                    </li>
                ))}
            </ul>

            <div className='mt-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
                <p className='max-w-2xl text-sm text-cool-grey-light'>
                    {platform.statusNote}
                </p>

                <a
                    href='#vision'
                    className='inline-flex min-h-11 items-center gap-2 self-start rounded-xl border border-soft-white/20 px-4 text-sm font-semibold text-soft-white transition hover:border-soft-white/40 hover:bg-navy-800 sm:self-auto'
                >
                    {platform.cta}
                    <ArrowRight className='size-4 text-orange' aria-hidden />
                </a>
            </div>
        </div>
    )
}
